<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container recu-modal" @click.stop>
      <!-- Header Premium -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-file-pdf"></i>
          </div>
          <div class="header-text">
            <h3>📄 Reçu de Caisse</h3>
            <p>{{ recu.numeroCaisse }}</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Contenu du reçu -->
      <div class="modal-body">
        <div class="recu-content" ref="recuContent">
          <!-- En-tête du reçu -->
          <div class="recu-header">
            <div class="company-info">
              <h2>AMANA</h2>
              <p>Management Suite</p>
            </div>
            <div class="recu-info">
              <h3>REÇU DE CAISSE</h3>
              <p><strong>N°:</strong> {{ recu.numeroCaisse }}</p>
              <p><strong>Date:</strong> {{ formatDate(recu.date) }}</p>
            </div>
          </div>

          <!-- Informations technicien -->
          <div class="section">
            <h4>👨‍🔧 Technicien</h4>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Nom :</span>
                <span class="value">{{ recu.technicien?.prenom }} {{ recu.technicien?.nom }}</span>
              </div>
              <div class="info-item">
                <span class="label">Date de remise :</span>
                <span class="value">{{ formatDateTime(recu.dateRemise) }}</span>
              </div>
            </div>
          </div>

          <!-- Montants déclarés -->
          <div class="section">
            <h4>💰 Montants Déclarés</h4>
            <div class="montants-table">
              <div class="table-row header">
                <div class="col">Type</div>
                <div class="col">Attendu</div>
                <div class="col">Déclaré</div>
                <div class="col">Écart</div>
              </div>
              <div class="table-row">
                <div class="col">💵 Espèces</div>
                <div class="col">{{ formatCurrency(recu.montantCashAttendu) }}</div>
                <div class="col">{{ formatCurrency(recu.montantCashDeclare) }}</div>
                <div class="col" :class="getEcartClass(recu.ecartCash)">
                  {{ formatCurrency(recu.ecartCash) }}
                </div>
              </div>
              <div class="table-row">
                <div class="col">📝 Chèques</div>
                <div class="col">{{ formatCurrency(recu.montantChequesAttendu) }}</div>
                <div class="col">{{ formatCurrency(recu.montantChequesDeclare) }}</div>
                <div class="col" :class="getEcartClass(recu.ecartCheques)">
                  {{ formatCurrency(recu.ecartCheques) }}
                </div>
              </div>
              <div class="table-row total">
                <div class="col"><strong>TOTAL</strong></div>
                <div class="col"><strong>{{ formatCurrency(recu.montantCashAttendu + recu.montantChequesAttendu) }}</strong></div>
                <div class="col"><strong>{{ formatCurrency(recu.montantCashDeclare + recu.montantChequesDeclare) }}</strong></div>
                <div class="col"><strong>{{ formatCurrency(recu.ecartCash + recu.ecartCheques) }}</strong></div>
              </div>
            </div>
          </div>

          <!-- Motifs d'écarts -->
          <div v-if="recu.motifEcart" class="section">
            <h4>⚠️ Motifs des Écarts</h4>
            <p class="motif-text">{{ recu.motifEcart }}</p>
          </div>

          <!-- Commentaires -->
          <div v-if="recu.commentaires || recu.commentairesFinancier" class="section">
            <h4>💬 Commentaires</h4>
            <div v-if="recu.commentaires" class="comment-block">
              <strong>Technicien :</strong>
              <p>{{ recu.commentaires }}</p>
            </div>
            <div v-if="recu.commentairesFinancier" class="comment-block">
              <strong>Financier :</strong>
              <p>{{ recu.commentairesFinancier }}</p>
            </div>
          </div>

          <!-- Signatures -->
          <div class="signatures-section">
            <div class="signature-block">
              <h5>Signature Technicien</h5>
              <div class="signature-box">
                <img v-if="recu.signatureTechnicien" :src="recu.signatureTechnicien" alt="Signature Technicien">
                <span v-else class="no-signature">Non signée</span>
              </div>
              <p class="signature-name">{{ recu.technicien?.prenom }} {{ recu.technicien?.nom }}</p>
            </div>
            <div class="signature-block">
              <h5>Signature Financier</h5>
              <div class="signature-box">
                <img v-if="recu.signatureFinancier" :src="recu.signatureFinancier" alt="Signature Financier">
                <span v-else class="no-signature">En attente</span>
              </div>
              <p class="signature-name">Service Financier</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="recu-footer">
            <p>Document généré automatiquement le {{ formatDateTime(new Date()) }}</p>
            <p>AMANA Management Suite - Système de Gestion Financière</p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">
          <i class="fas fa-times"></i>
          Fermer
        </button>
        <button @click="downloadPDF" class="btn-download">
          <i class="fas fa-download"></i>
          Télécharger PDF
        </button>
        <button @click="printRecu" class="btn-print">
          <i class="fas fa-print"></i>
          Imprimer
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecuCaisseModal',
  props: {
    recu: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    
    const closeModal = () => {
      emit('close')
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const formatDateTime = (date) => {
      return new Date(date).toLocaleString('fr-FR')
    }

    const getEcartClass = (ecart) => {
      if (ecart > 0) return 'surplus'
      if (ecart < 0) return 'deficit'
      return 'equilibre'
    }

    const downloadPDF = () => {
      // Utiliser html2pdf ou jsPDF pour générer le PDF
      window.print()
    }

    const printRecu = () => {
      window.print()
    }

    return {
      closeModal,
      formatCurrency,
      formatDate,
      formatDateTime,
      getEcartClass,
      downloadPDF,
      printRecu
    }
  }
}
</script>

<style scoped>
.recu-modal {
  max-width: 900px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
}

.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
}

/* Contenu du reçu */
.recu-content {
  padding: 2rem;
  background: white;
  font-family: 'Arial', sans-serif;
}

.recu-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--primary-blue);
}

.company-info h2 {
  margin: 0;
  color: var(--primary-blue);
  font-size: 2rem;
  font-weight: 700;
}

.company-info p {
  margin: 0;
  color: #6c757d;
  font-style: italic;
}

.recu-info {
  text-align: right;
}

.recu-info h3 {
  margin: 0 0 0.5rem 0;
  color: var(--primary-blue);
  font-size: 1.5rem;
}

.recu-info p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
}

/* Sections */
.section {
  margin-bottom: 2rem;
  background: rgba(248, 250, 252, 0.5);
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid var(--primary-blue);
}

.section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-size: 1.1rem;
  font-weight: 700;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
}

.info-item .label {
  font-weight: 600;
  color: #6c757d;
}

.info-item .value {
  font-weight: 500;
  color: #1a202c;
}

/* Tableau des montants */
.montants-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
}

.table-row.header {
  background: var(--primary-blue);
  color: white;
  font-weight: 700;
}

.table-row.total {
  background: rgba(0, 27, 183, 0.1);
  border-top: 2px solid var(--primary-blue);
}

.col {
  padding: 1rem;
  text-align: center;
  border-right: 1px solid #e2e8f0;
}

.col:last-child {
  border-right: none;
}

.surplus {
  color: #10b981;
  font-weight: 700;
}

.deficit {
  color: #ef4444;
  font-weight: 700;
}

.equilibre {
  color: #6c757d;
}

/* Motifs et commentaires */
.motif-text {
  background: rgba(239, 68, 68, 0.1);
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #ef4444;
  margin: 0;
}

.comment-block {
  margin-bottom: 1rem;
}

.comment-block strong {
  color: var(--primary-blue);
}

.comment-block p {
  margin: 0.5rem 0 0 0;
  padding: 0.75rem;
  background: rgba(0, 27, 183, 0.05);
  border-radius: 8px;
}

/* Signatures */
.signatures-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.signature-block {
  text-align: center;
}

.signature-block h5 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-weight: 600;
}

.signature-box {
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.signature-box img {
  max-width: 100%;
  max-height: 100%;
}

.no-signature {
  color: #6c757d;
  font-style: italic;
}

.signature-name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a202c;
}

/* Footer du reçu */
.recu-footer {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  color: #6c757d;
  font-size: 0.8rem;
}

.recu-footer p {
  margin: 0.25rem 0;
}

/* Footer du modal */
.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-radius: 0 0 20px 20px;
}

.btn-cancel,
.btn-download,
.btn-print {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-download {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-print {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-cancel:hover,
.btn-download:hover,
.btn-print:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Print styles */
@media print {
  .modal-overlay {
    position: static;
    background: white;
  }
  
  .modal-container {
    box-shadow: none;
    border-radius: 0;
    max-width: none;
    width: 100%;
  }
  
  .modal-header,
  .modal-footer {
    display: none;
  }
  
  .recu-content {
    padding: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .recu-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .signatures-section {
    grid-template-columns: 1fr;
  }
  
  .table-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .col {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }
}
</style>
